import { ASSET_LIST_PAGE_SIZE } from '~/services/partner-management-service/constants';
import GatewayAxiosClient from '../GatewayAxiosClient';
import type {
  FetchAssetParams,
  CarrierAccount,
  CarrierAccountUpdate,
  CarrierAccountCreate,
  UpdateLabelAugmentation,
  CreateLabelAugmentation,
  UpdateLabelAugmentationTagging,
  FulfillmentContext,
  FulfillmentContextCreate,
  FulfillmentContextUpdate,
  Schedule,
  CarrierSetting,
  Contract,
  FulfillmentContextLinkPropertiesCreate,
  FulfillmentContextLinkProperty,
  LabelProperties,
  LabelAugmentationSet,
  CreateAugmentationSetWithLinks,
  PackagingSet,
  Packaging,
  ReturnsSettings,
  EditAugmentationSetWithLinks,
  ScheduleCreate,
  ScheduleUpdate,
  TenantLinkUpdate,
  ThirdPartyBillingAccountCreate,
  ThirdPartyBillingAccountUpdate,
  CarrierAccountSearchParams,
  CarrierAccountSearchResponse,
  FetchCarrierRatesParams,
  ReturnToAddressOverride,
  FetchReturnToAddressOverrideParams,
  BaseAsset,
  LinkCarrierRateSetToContractParams,
  FetchCarrierShipmentLaneConfigurationsParams,
} from '../../assets/types';
import type {
  AssetActionPayload,
  AssetActionPayloadLink,
  AssetActionPayloadResult,
  AssetActionType,
  AssetTypes,
  WorkflowActionError,
} from '../../workflow/types';
import type { AxiosError, Method } from 'axios';
import { AssetWorkflow } from '../../workflow/AssetWorkflow';
import { addCarrierScheduleFromWorkflowData, deleteCarrierScheduleFromWorkflowData } from './carrierScheduleUtils';
import { GLOBAL_TENANT } from '../../../components/workflow/tenant/GlobalTenantSelect';
import type {
  AddressType,
  ContactType,
  ContractCreateParams,
  ContractUpdateParams,
  LinkAddressParams,
  LinkContactParams,
} from '../../fulfillmentContext/types';
import type { PartnerLaneOrchestration } from '@shipium-corp/shipium-ts-types';
import type {
  PartnerAddressV2,
  PartnerCarrierRate,
  PartnerCarrierRateCreate,
  PartnerCarrierRateSet,
  PartnerCarrierRateSetCreate,
  PartnerTag,
  PartnerTagCreate,
  PartnerTagUpdate,
  PartnerTenant,
  PartnerTenantCreate,
  PartnerTenantUpdate,
  PartnerThirdPartyBillingAccountSet,
  PartnerContactCreate,
  PartnerContactUpdate,
  PartnerContact,
  PartnerOrigin,
  PartnerOriginUpdate,
  PartnerOriginCreate,
  PartnerAddressBookCreate,
  PartnerAddressBook,
  PartnerAddressBookPatch,
  PartnerAddressUpsert,
} from '@shipium-corp/shipium-ts-types';

export class AssetService {
  public async fetchTenants(params: FetchAssetParams = {}): Promise<PartnerTenant[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/tenants', {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async fetchTenant(tenantId: string): Promise<PartnerTenant> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/tenants/${tenantId}`);
    return res.data;
  }

  public async createTenant(tenant: PartnerTenantCreate): Promise<PartnerTenant> {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/tenants', tenant);
    return res.data;
  }

  public async updateTenant({
    tenantId,
    tenant,
  }: {
    tenantId: string;
    tenant: PartnerTenantUpdate;
  }): Promise<PartnerTenant> {
    const res = await GatewayAxiosClient.put(`/partner-management-proxy/api/v1/partners/tenants/${tenantId}`, tenant);
    return res.data;
  }

  public async createTenantLink(tenantId: string, tenantLinkCreate: TenantLinkUpdate): Promise<PartnerTenant> {
    const res = await GatewayAxiosClient(`/partner-management-proxy/api/v1/partners/tenants/${tenantId}/link`, {
      method: 'PUT',
      data: tenantLinkCreate,
    });
    return res.data;
  }

  public async deleteTenantLink(tenantId: string, tenantLinkDelete: TenantLinkUpdate): Promise<PartnerTenant> {
    const res = await GatewayAxiosClient(`/partner-management-proxy/api/v1/partners/tenants/${tenantId}/unlink`, {
      method: 'PUT',
      data: tenantLinkDelete,
    });
    return res.data;
  }

  public async linkTenantUsppiAddress({
    tenantId,
    usppiAddressId,
  }: {
    tenantId: string;
    usppiAddressId: string;
  }): Promise<PartnerTenant> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/tenants/${tenantId}/usppiAddresses/${usppiAddressId}`,
    );
    return res.data;
  }

  public async unlinkTenantUsppiAddress({
    tenantId,
    usppiAddressId,
  }: {
    tenantId: string;
    usppiAddressId: string;
  }): Promise<PartnerTenant> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/tenants/${tenantId}/usppiAddresses/${usppiAddressId}`,
    );
    return res.data;
  }

  public async linkTenantUsppiContact({
    tenantId,
    usppiContactId,
  }: {
    tenantId: string;
    usppiContactId: string;
  }): Promise<PartnerTenant> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/tenants/${tenantId}/usppiContacts/${usppiContactId}`,
    );
    return res.data;
  }

  public async unlinkTenantUsppiContact({
    tenantId,
    usppiContactId,
  }: {
    tenantId: string;
    usppiContactId: string;
  }): Promise<PartnerTenant> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/tenants/${tenantId}/usppiContacts/${usppiContactId}`,
    );
    return res.data;
  }

  // Returns Settings

  public async fetchReturnsSettings(params: FetchAssetParams): Promise<ReturnsSettings[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/returnsSettings', {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async createReturnsSettings(returnsSettings: ReturnsSettings): Promise<ReturnsSettings> {
    const res = await GatewayAxiosClient.post(
      `/partner-management-proxy/api/v1/partners/returnsSettings`,
      returnsSettings,
    );
    return res.data;
  }

  public async updateReturnsSettings(
    returnsSettings: ReturnsSettings,
    returnsSettingsId: string,
  ): Promise<ReturnsSettings> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/returnsSettings/${returnsSettingsId}`,
      returnsSettings,
    );
    return res.data;
  }

  public async updateReturnsSettingsAddress(
    returnsSettingsId: string,
    returnToAddressId: string,
  ): Promise<ReturnsSettings> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/returnsSettings/${returnsSettingsId}/returnToAddress/${returnToAddressId}`,
    );
    return res.data;
  }

  public async updateReturnsSettingsContact(
    returnsSettingsId: string,
    returnToContactInfoId: string,
  ): Promise<ReturnsSettings> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/returnsSettings/${returnsSettingsId}/returnToContactInfo/${returnToContactInfoId}`,
    );
    return res.data;
  }

  // packaging

  public async fetchPackagingSets(params: FetchAssetParams): Promise<PackagingSet[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/packagingSets`, {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async fetchPackagings(params: FetchAssetParams): Promise<Packaging[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/packaging`, {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  // augmentation sets
  public async createLabelAugmentationsSetWithLinks(
    augmentationLinkCreate: CreateAugmentationSetWithLinks,
  ): Promise<LabelAugmentationSet> {
    const res = await GatewayAxiosClient.post(`/partner-configuration/augmentationSets`, augmentationLinkCreate);
    return res.data;
  }

  public async editLabelAugmentationsSetWithLinks(
    augmentationLinkUpdate: EditAugmentationSetWithLinks,
    augmentationSetId: string,
  ): Promise<LabelAugmentationSet> {
    const res = await GatewayAxiosClient.put(
      `/partner-configuration/augmentationSets/${augmentationSetId}`,
      augmentationLinkUpdate,
    );
    return res.data;
  }

  public async editCarrierRateSet(
    carrierRateSet: PartnerCarrierRateSet,
    carrierRateSetId: string,
  ): Promise<PartnerCarrierRateSet> {
    const res = await GatewayAxiosClient.put(
      `/partner-configuration/carrierRateSets/${carrierRateSetId}`,
      carrierRateSet,
    );
    return res.data;
  }

  public async fetchLabelAugmentationSets(params: FetchAssetParams): Promise<LabelAugmentationSet[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/augmentationSets`, {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  // label properties
  public async fetchLabelProperties(params: FetchAssetParams): Promise<LabelProperties[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/labelProperties`, {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  // contracts
  public async fetchCarrierContract(contractId: string): Promise<Contract> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/contracts/${contractId}`);
    return res.data;
  }

  public async fetchCarrierContracts(params: FetchAssetParams): Promise<Contract[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/contracts`, {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async createContract(payload: ContractCreateParams) {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/contracts', payload);
    return res.data;
  }

  public async updateContract(payload: ContractUpdateParams) {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/contracts/${payload.contractId}`,
      payload,
    );
    return res.data;
  }
  // carrier rate sets

  public async updateCarrierRateSetLinking(
    carrierRateSetId: string,
    carrierRateId: string,
  ): Promise<PartnerCarrierRateSet> {
    const res = await GatewayAxiosClient(
      `/partner-management-proxy/api/v1/partners/carrierRateSets/${carrierRateSetId}/carrierRates/${carrierRateId}`,
      {
        method: 'PUT',
      },
    );
    return res.data;
  }

  public async createCarrierRateSet(carrierRateSetCreate: PartnerCarrierRateSetCreate): Promise<PartnerCarrierRateSet> {
    const res = await GatewayAxiosClient.post(
      '/partner-management-proxy/api/v1/partners/carrierRateSets',
      carrierRateSetCreate,
    );
    return res.data;
  }

  public async fetchCarrierRateSet(carrierRateSetId: string): Promise<PartnerCarrierRateSet> {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partners/carrierRateSets/${carrierRateSetId}`,
    );
    return res.data;
  }

  public async fetchCarrierRateSets(params: FetchAssetParams): Promise<PartnerCarrierRateSet[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/carrierRateSets', {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async linkCarrierRateSetToContract(payload: LinkCarrierRateSetToContractParams) {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/contracts/` +
        `${payload.contractId}/carrierRateSets/` +
        `${payload.carrierRateSetId}`,
    });
  }

  // carrier settings
  public async fetchCarrierSetting(carrierSettingId: string): Promise<CarrierSetting> {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partners/carrierSettings/${carrierSettingId}`,
    );
    return res.data;
  }

  public async fetchCarrierSettings(params: FetchAssetParams): Promise<CarrierSetting[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/carrierSettings`, {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }
  public async createCarrierSettings(payload: Partial<CarrierSetting>): Promise<CarrierSetting> {
    return (await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/partners/carrierSettings`, payload)).data;
  }
  public async updateCarrierSettings(payload: Partial<CarrierSetting>): Promise<CarrierSetting> {
    return (
      await GatewayAxiosClient.put(
        `/partner-management-proxy/api/v1/partners/carrierSettings/${payload.carrierSettingsId}`,
        payload,
      )
    ).data;
  }

  // schedule

  public async createSchedule(schedule: ScheduleCreate): Promise<Schedule> {
    const res = await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/partners/schedules`, schedule);
    return res.data;
  }

  public async updateSchedule(scheduleUpdate: ScheduleUpdate): Promise<Schedule> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/schedules/${scheduleUpdate.scheduleId}`,
      scheduleUpdate,
    );
    return res.data;
  }

  public async fetchSchedule(scheduleId: string): Promise<Schedule> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/schedules/${scheduleId}`);
    return res.data;
  }

  public async fetchSchedules(params: FetchAssetParams): Promise<Schedule[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/schedules`, {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  // Fulfillment Context Link Properties

  public async fetchFulfillmentContextLinkProperties(
    params: FetchAssetParams,
  ): Promise<FulfillmentContextLinkProperty[]> {
    const res = await GatewayAxiosClient.get(
      '/partner-management-proxy/api/v1/partners/fulfillmentContextLinkProperties',
      {
        params,
      },
    );
    return res.data;
  }

  public async fetchFulfillmentContextLinkProperty(
    fulfillmentContextId: string,
  ): Promise<FulfillmentContextLinkProperty> {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partners/fulfillmentContextLinkProperties/${fulfillmentContextId}`,
    );
    return res.data;
  }

  public async createFulfillmentContextLinkProperty(
    createFulfillmentContextLinkProperties: FulfillmentContextLinkPropertiesCreate,
  ): Promise<FulfillmentContextLinkProperty> {
    const res = await GatewayAxiosClient.post(
      '/partner-management-proxy/api/v1/partners/fulfillmentContextLinkProperties',
      createFulfillmentContextLinkProperties,
    );
    return res.data;
  }

  public async deactivateFulfillmentContextProperty(fulfillmentContextLinkPropertiesId: string): Promise<any> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/fulfillmentContextLinkProperties/${fulfillmentContextLinkPropertiesId}`,
    );
    return res.data;
  }

  // Fulfillment Context

  public async fetchFulfillmentContexts(params: FetchAssetParams): Promise<FulfillmentContext[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/fulfillmentContexts', {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }
  public async fetchFulfillmentContext(fulfillmentContextId: string): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}`,
    );
    return res.data;
  }

  public async createFulfillmentContext(
    createFulfillmentContext: FulfillmentContextCreate,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.post(
      '/partner-management-proxy/api/v1/partners/fulfillmentContexts',
      createFulfillmentContext,
    );
    return res.data;
  }

  public async updateFulfillmentContext(
    updateFulfillmentContext: FulfillmentContextUpdate,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${updateFulfillmentContext.fulfillmentContextId}`,
      updateFulfillmentContext,
    );
    return res.data;
  }

  public async updateFulfillmentContextAugmentationSetLinking(
    fulfillmentContextId: string,
    augmentationSetId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/augmentationSets/${augmentationSetId}`,
      null,
    );
    return res.data;
  }

  public async deleteFulfillmentContextAugmentationSetLinking(
    fulfillmentContextId: string,
    augmentationSetId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/augmentationSets/${augmentationSetId}`,
    );
    return res.data;
  }

  public async updateFulfillmentContextContractLinking(
    fulfillmentContextId: string,
    contractId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/contracts/${contractId}`,
      null,
    );
    return res.data;
  }

  public async deleteFulfillmentContextContractLinking(
    fulfillmentContextId: string,
    contractId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/contracts/${contractId}`,
    );
    return res.data;
  }

  public async updateFulfillmentContextLabelLinking(
    fulfillmentContextId: string,
    labelPropertiesId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/labelProperties/${labelPropertiesId}`,
      null,
    );
    return res.data;
  }

  public async deleteFulfillmentContextLabelLinking(
    fulfillmentContextId: string,
    labelPropertiesId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/labelProperties/${labelPropertiesId}`,
    );
    return res.data;
  }

  public async updateFulfillmentContextPackageLinking(
    fulfillmentContextId: string,
    packagingSetId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.put(
      `/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/packagingSets/${packagingSetId}`,
      null,
    );
    return res.data;
  }

  public async deleteFulfillmentContextPackageLinking(
    fulfillmentContextId: string,
    packagingSetId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.delete(
      `/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/packagingSets/${packagingSetId}`,
    );
    return res.data;
  }

  public async updateFulfillmentContextReturnSettingsLinking(
    fulfillmentContextId: string,
    returnSettingsId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/returnsSettings/${returnSettingsId}`,
    );
    return res.data;
  }

  public async deleteFulfillmentContextReturnSettingsLinking(
    fulfillmentContextId: string,
    returnSettingsId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/returnsSettings/${returnSettingsId}`,
    );
    return res.data;
  }

  public async updateFulfillmentContextScheduleLinking(
    fulfillmentContextId: string,
    scheduleId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/schedules/${scheduleId}`,
      null,
    );
    return res.data;
  }

  public async deleteFulfillmentContextScheduleLinking(
    fulfillmentContextId: string,
    scheduleId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/schedules/${scheduleId}`,
    );
    return res.data;
  }

  public async updateFulfillmentContextTagging(
    fulfillmentContextId: string,
    tagId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/tags/${tagId}`,
      null,
    );
    return res.data;
  }

  public async deleteFulfillmentContextTagging(
    fulfillmentContextId: string,
    tagId: string,
  ): Promise<FulfillmentContext> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/fulfillmentContexts/${fulfillmentContextId}/tags/${tagId}`,
    );
    return res.data;
  }

  // Carrier Rates
  public async fetchCarrierRates(params: FetchCarrierRatesParams): Promise<PartnerCarrierRate[]> {
    const { carrierRateType, ...restParams } = params;
    const modifiedParams = carrierRateType === 'sell' ? { ...restParams, carrierRateType } : restParams;
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/carrierRates`, {
      params: modifiedParams,
    });
    return res.data;
  }

  public async createCarrierRates(carrierRateCreate: PartnerCarrierRateCreate): Promise<PartnerCarrierRate> {
    const res = await GatewayAxiosClient.post(
      '/partner-management-proxy/api/v1/partners/carrierRates',
      carrierRateCreate,
    );
    return res.data;
  }

  public async archiveCarrierRate(carrierRateId: string): Promise<void> {
    await GatewayAxiosClient.put(`/partner-management-proxy/api/v1/partners/carrierRates/${carrierRateId}`, {
      active: false,
      archived: true,
    });
  }

  //TODO: update type to ShiptimeCarrierRateSetCurrencyUpdateResponse once type is available
  public async updateCarrierRateCurrency(carrierRateSetId: string, currencyCode: string): Promise<any> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/carrierRateSets/${carrierRateSetId}/currency/${currencyCode}`,
    );
    return res.data;
  }

  //contacts
  public async fetchContacts(params: FetchAssetParams): Promise<PartnerContact[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/contacts', {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async fetchContact(contactId: string): Promise<PartnerContact> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/contacts/${contactId}`);
    return res.data;
  }

  public async updateContact({
    contactId,
    payload,
  }: {
    contactId: string;
    payload: PartnerContactUpdate;
  }): Promise<PartnerContact> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/contacts/${contactId}`,
      payload,
    );
    return res.data;
  }

  public async createContact(contactCreate: PartnerContactCreate): Promise<PartnerContact> {
    const res = await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/partners/contacts`, contactCreate);
    return res.data;
  }

  public async linkContact(contactLinkParams: LinkContactParams): Promise<PartnerContact> {
    const baseType: ContactType = 'base';
    const currentContactType = contactLinkParams.contactType;
    const res = await GatewayAxiosClient({
      method: contactLinkParams.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/contacts/` +
        `${currentContactType === baseType ? '' : currentContactType + '/'}` +
        `${contactLinkParams.contactId}/` +
        `${contactLinkParams.assetLinkType}/` +
        `${contactLinkParams.assetId}`,
    });

    return res.data;
  }

  public async updateContactTagging(contactId: string, tagId: string): Promise<PartnerContact> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/contacts/${contactId}/tags/${tagId}`,
      null,
    );
    return res.data;
  }

  public async deleteContactTagging(contactId: string, tagId: string): Promise<PartnerContact> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/contacts/${contactId}/tags/${tagId}`,
      null,
    );
    return res.data;
  }

  //addresses
  public async fetchAddresses(params: FetchAssetParams): Promise<PartnerAddressV2[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/addresses', {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async fetchAddress(addressId: string): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/addresses/${addressId}`);
    return res.data;
  }

  public async updateAddress({
    addressId,
    payload: payload,
  }: {
    addressId: string;
    payload: PartnerAddressUpsert;
  }): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/addresses/${addressId}`,
      payload,
    );
    return res.data;
  }

  public async createAddress(addressCreate: PartnerAddressUpsert): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/addresses', addressCreate);
    return res.data;
  }

  public async createAddressBook(addressCreate: PartnerAddressBookCreate): Promise<PartnerAddressBook> {
    const res = await GatewayAxiosClient.post(
      '/partner-management-proxy/api/v1/partners/book/addresses',
      addressCreate,
    );
    return res.data;
  }

  public async updateAddressBook({
    addressId,
    payload,
  }: {
    addressId: string;
    payload: PartnerAddressBookPatch;
  }): Promise<PartnerAddressBook> {
    const res = await GatewayAxiosClient.patch(
      `/partner-management-proxy/api/v1/partners/book/addresses/${addressId}`,
      payload,
    );
    return res.data;
  }

  public async updateAddressTagging(addressId: string, tagId: string): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/addresses/${addressId}/tags/${tagId}`,
      null,
    );
    return res.data;
  }

  public async deleteAddressTagging(addressId: string, tagId: string): Promise<PartnerAddressV2> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/addresses/${addressId}/tags/${tagId}`,
    );
    return res.data;
  }

  public async linkAddress(addressLinkParams: LinkAddressParams): Promise<PartnerAddressV2> {
    const baseType: AddressType = 'base';
    const currentAddressType = addressLinkParams.addressType;
    const res = await GatewayAxiosClient({
      method: addressLinkParams.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/addresses/` +
        `${baseType === currentAddressType ? '' : currentAddressType + '/'}` +
        `${addressLinkParams.addressId}/` +
        `${addressLinkParams.assetLinkType}/` +
        `${addressLinkParams.assetId}`,
    });

    return res.data;
  }

  // origins
  public async fetchOrigins(params: FetchAssetParams): Promise<PartnerOrigin[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/origins', {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async fetchOrigin(originId: string): Promise<PartnerOrigin> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/origins/${originId}`);
    return res.data;
  }

  public async updateOrigin({
    originId,
    payload,
  }: {
    originId: string;
    payload: PartnerOriginUpdate;
  }): Promise<PartnerOrigin> {
    const res = await GatewayAxiosClient.put(`/partner-management-proxy/api/v1/partners/origins/${originId}`, payload);
    return res.data;
  }

  public async createOrigin(payload: PartnerOriginCreate): Promise<PartnerOrigin> {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/origins', payload);
    return res.data;
  }

  // label augmentations

  public async fetchLabelAugmentations(params: FetchAssetParams) {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/labelAugmentations', {
      params,
    });
    return res.data;
  }
  public async updateLabelAugmentation(payload: UpdateLabelAugmentation) {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/labelAugmentations/${payload.labelAugmentationId}`,
      payload,
    );
    return res.data;
  }

  public async createLabelAugmentation(payload: CreateLabelAugmentation) {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/labelAugmentations', payload);
    return res.data;
  }

  public async deleteLabelAugmentationTagging(payload: UpdateLabelAugmentationTagging) {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/labelAugmentations/${payload.labelAugmentationId}/tags/${payload.tagId}`,
    );
    return res.data;
  }

  public async updateLabelAugmentationTagging(payload: UpdateLabelAugmentationTagging) {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/labelAugmentations/${payload.labelAugmentationId}/tags/${payload.tagId}`,
      payload,
    );
    return res.data;
  }
  // carrier accounts
  public async createCarrierAccount(carrierAccountCreate: CarrierAccountCreate): Promise<CarrierAccount> {
    const res = await GatewayAxiosClient.post(
      '/partner-management-proxy/api/v2/partners/carrierAccounts',
      carrierAccountCreate,
    );
    return res.data;
  }

  public async fetchCarrierAccounts(params: FetchAssetParams): Promise<CarrierAccount[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v2/partners/carrierAccounts', {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async searchCarrierAccounts(params: CarrierAccountSearchParams): Promise<CarrierAccountSearchResponse[]> {
    const res = await GatewayAxiosClient.get(`/partners/carrierAccounts/search`, {
      params,
    });
    return res.data;
  }

  public async updateCarrierAccount(carrierAccount: CarrierAccountUpdate): Promise<CarrierAccount> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v2/partners/carrierAccounts/${carrierAccount.carrierAccountId}`,
      carrierAccount,
    );
    return res.data;
  }

  public async updateCarrierAccountTagging(carrierAccountId: string, tagId: string): Promise<CarrierAccount> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v2/partners/carrierAccounts/${carrierAccountId}/tags/${tagId}`,
    );
    return res.data;
  }

  public async deleteCarrierAccountTagging(carrierAccountId: string, tagId: string): Promise<CarrierAccount> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v2/partners/carrierAccounts/${carrierAccountId}/tags/${tagId}`,
    );
    return res.data;
  }

  // returnToAddressOverrides

  public async fetchReturnToAddressOverrides(
    params: FetchReturnToAddressOverrideParams,
  ): Promise<ReturnToAddressOverride[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/returnToAddressOverrides', {
      params,
    });
    return res.data;
  }

  // shipmentLanes

  public async fetchShipmentLanes(
    params: FetchCarrierShipmentLaneConfigurationsParams,
  ): Promise<PartnerLaneOrchestration[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partnerConfiguration/lanes', {
      params,
    });
    return res.data.results;
  }

  // tagging
  public async fetchTags(params?: FetchAssetParams): Promise<PartnerTag[]> {
    const res = await GatewayAxiosClient.get('/partner-management-proxy/api/v1/partners/tags', {
      params: { count: ASSET_LIST_PAGE_SIZE, ...params },
    });
    return res.data;
  }

  public async fetchTag(id: string): Promise<PartnerTag> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/tags/${id}`);
    return res.data;
  }

  public async createTag(payload: PartnerTagCreate): Promise<PartnerTag> {
    const res = await GatewayAxiosClient.post('/partner-management-proxy/api/v1/partners/tags', payload);
    return res.data;
  }

  public async updateTag({ tagId, payload }: { tagId: string; payload: PartnerTagUpdate }): Promise<PartnerTag> {
    const res = await GatewayAxiosClient.put(`/partner-management-proxy/api/v1/partners/tags/${tagId}`, payload);
    return res.data;
  }

  public async fetchThirdPartyBillingAccounts() {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/thirdPartyBillingAccounts`, {
      params: {
        asOfDateTime: new Date().toISOString(),
      },
    });
    return res.data;
  }

  public async fetchThirdPartyBillingAccount(thirdPartyBillingAccontId: string) {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partners/thirdPartyBillingAccounts/${thirdPartyBillingAccontId}`,
    );
    return res.data;
  }

  public async createThirdPartyBillingAccount(thirdPartyBillingCreate: ThirdPartyBillingAccountCreate) {
    const res = await GatewayAxiosClient.post(
      `/partner-management-proxy/api/v1/partners/thirdPartyBillingAccounts`,
      thirdPartyBillingCreate,
    );
    return res.data;
  }

  public async updateThirdPartyBillingAccount(thirdPartyBillingUpdate: ThirdPartyBillingAccountUpdate) {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/thirdPartyBillingAccounts/${thirdPartyBillingUpdate.thirdPartyBillingAccountId}`,
      thirdPartyBillingUpdate,
    );
    return res.data;
  }

  public async fetchThirdPartyBillingAccountSets(params?: FetchAssetParams) {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/thirdPartyBillingAccountSets`, {
      params,
    });
    return res.data;
  }

  public async fetchThirdPartyBillingAccountSet(
    thirdPartyBillingAccountSetId: string,
  ): Promise<Required<PartnerThirdPartyBillingAccountSet>> {
    const res = await GatewayAxiosClient.get(
      `/partner-management-proxy/api/v1/partners/thirdPartyBillingAccountSets/${thirdPartyBillingAccountSetId}`,
    );
    return res.data;
  }

  public static assetTypeToAssetUrlName(assetType: AssetTypes) {
    switch (assetType) {
      case 'origin':
        return 'origins';
      case 'address':
        return 'addresses';
      case 'contact':
        return 'contacts';
      case 'carrierRate':
        return 'carrierRates';
      case 'carrierRateSet':
        return 'carrierRateSets';
      case 'schedule':
        return 'schedules';
      case 'carrierSettings':
        return 'carrierSettings';
      case 'carrierAccount':
        return 'carrierAccounts';
      case 'contract':
        return 'contracts';
      case 'fulfillmentContext':
        return 'fulfillmentContexts';
      case 'fulfillmentContextLinkProperty':
        return 'fulfillmentContextLinkProperties';
      case 'tags':
        return 'tags';
      case 'labelProperties':
        return 'labelProperties';
      case 'labelAugmentation':
        return 'labelAugmentations';
      case 'augmentationSet':
        return 'augmentationSets';
      case 'packaging':
        return 'packaging';
      case 'packagingSet':
        return 'packagingSets';
      case 'returnsSettings':
        return 'returnsSettings';
      case 'tenant':
        return 'tenants';
      case 'thirdPartyBillingAccount':
        return 'thirdPartyBillingAccounts';
      case 'thirdPartyBillingAccountSet':
        return 'thirdPartyBillingAccountSets';
      case 'returnAddressOverride':
        return 'returnToAddressOverrides';
      case 'laneSortConfigurations':
        return 'laneSortConfigurations';
      default:
        throw new Error('Unmapped asset Type');
    }
  }

  public async handleAssetActionPayload(assetActions: AssetActionPayload): Promise<AssetActionPayloadResult> {
    try {
      const { assetType, assetPayload, assetActionType, assetId, overrideSave } = assetActions;
      let { assetLinks } = assetActions;
      if (overrideSave) {
        switch (overrideSave) {
          case 'addCarrierSchedule':
            if (assetId && assetPayload) {
              // doing this in multiple steps to make more testable
              const currentSchedule = await this.fetchSchedule(assetId);
              const updatedSchedule = addCarrierScheduleFromWorkflowData(assetPayload as any, currentSchedule);
              return { ...assetActions, assetResult: await this.updateSchedule(updatedSchedule) };
            }
            throw new Error('Must provide schedule id');
          case 'editThirdPartyBillingAccountSet':
            if (assetId) {
              // sigh
              // in order to update the billing account we need remove all of the billing accounts
              // that are no longer part of the set
              const currentThirdPartAccount = await this.fetchThirdPartyBillingAccountSet(assetId);
              const accountsToRemove = currentThirdPartAccount.thirdPartyBillingAccountIds.filter((act) => {
                return !assetLinks.find((l) => l.assetId === act);
              });
              // do not try to relink accounts that are already linked
              assetLinks = assetLinks.filter(
                (l) => !currentThirdPartAccount.thirdPartyBillingAccountIds.includes(l.assetId as string),
              );
              await Promise.all(
                accountsToRemove.map((act) =>
                  this.handleLinkAction('thirdPartyBillingAccountSet', assetId, {
                    unlink: true,
                    assetId: act,
                    assetType: 'thirdPartyBillingAccount',
                  }),
                ),
              );
            }
            break;
          case 'deleteCarrierSchedule':
            if (assetId && assetPayload) {
              // doing this in multiple steps to make more testable
              const currentSchedule = await this.fetchSchedule(assetId);
              const updatedSchedule = deleteCarrierScheduleFromWorkflowData(assetPayload as any, currentSchedule);
              return { ...assetActions, assetResult: await this.updateSchedule(updatedSchedule) };
            }
            throw new Error('Must provide schedule id');
          case 'editFulfillmentContext': {
            const currentContext = await this.fetchFulfillmentContext(assetId as string);
            // remove third party billing groups that are no longer in the context
            const linksToDelete =
              currentContext?.thirdPartyBillingAccountSetIds?.reduce((delLinks: AssetActionPayloadLink[], val) => {
                if (!assetLinks.find((assetLink) => assetLink.assetId === val)) {
                  delLinks = [
                    ...delLinks,
                    {
                      assetId: val,
                      linkAssetTypeOverride: 'thirdPartyBillingAccountSet',
                      assetType: 'thirdPartyBillingAccountSet',
                      unlink: true,
                    },
                  ];
                }
                return delLinks;
              }, []) ?? [];
            assetLinks = [...linksToDelete, ...assetLinks];
            // are actually not changing the entire save just modifiy the links (and potentially creating a set )
            // so instead of returning we just break out of the switch
            break;
          }
          case 'addAugmentationSet':
            return {
              ...assetActions,
              assetResult: await this.createLabelAugmentationsSetWithLinks(
                assetPayload as CreateAugmentationSetWithLinks,
              ),
            };
          case 'editAugmentationSet':
            return {
              ...assetActions,
              assetResult: await this.editLabelAugmentationsSetWithLinks(
                assetPayload as EditAugmentationSetWithLinks,
                assetId || '',
              ),
            };
          case 'editCarrierRateSet':
            return {
              ...assetActions,
              assetResult: (await this.editCarrierRateSet(
                assetPayload as PartnerCarrierRateSet,
                assetId || '',
              )) as BaseAsset,
            };
          case 'updatePackagingSetWorkflow':
            if (assetId) {
              // remove packagingIds that are no longer part of the set
              const packagingSets = await this.fetchPackagingSets({});
              const currentPackagingSet = packagingSets.find((p) => p.packagingSetId === assetId);
              if (!currentPackagingSet) {
                throw new Error(`Packaging Set ${assetId} not found`);
              }
              const packagingsToRemove = currentPackagingSet.packagingIds.filter((act) => {
                return !assetLinks.find((l) => l.assetId === act);
              });
              // do not try to relink accounts that are already linked
              assetLinks = assetLinks.filter((l) => !currentPackagingSet.packagingIds.includes(l.assetId as string));
              await Promise.all(
                packagingsToRemove.map((packagingId) => {
                  return GatewayAxiosClient.delete(
                    `/partner-management-proxy/api/v1/partners/packagingSets/${currentPackagingSet.packagingSetId}/packagings/${packagingId}`,
                  );
                }),
              );
            }
            break;
          default:
            throw new Error('No override function found');
        }
      }
      const baseRoute = `/partner-management-proxy/api/${this.checkV2BaseRoute(assetType, assetActionType) ? 'v2' : 'v1'}/${assetType === 'laneSortConfigurations' ? 'partnerConfiguration' : 'partners'}`;
      const assetUrlName = AssetService.assetTypeToAssetUrlName(assetType);
      const assetIdUrlPath = assetActionType !== 'add' ? `/${assetId}` : '';
      const assetActionToAxiosMethod: Record<AssetActionType, Method> = {
        add: 'POST',
        update: 'PUT',
        delete: 'DELETE',
      };
      const assetResult: any = await GatewayAxiosClient(`${baseRoute}/${assetUrlName}${assetIdUrlPath}`, {
        method: assetActionToAxiosMethod[assetActionType],
        data: assetPayload,
      });
      const assetResId = assetResult.data?.[AssetWorkflow.getAssetIdKeyForAssetType(assetType)];
      // tenant linking should always occur first
      // to avoid the service rejecting links to child assets that are tenant associated
      // see https://shipium.atlassian.net/browse/FE-630
      const sortedAssetLinks = assetLinks.sort((a, b) => {
        const aVal = a.linkType === 'Tenant' ? 1 : -1;
        const bVal = b.linkType === 'Tenant' ? 1 : -1;
        return bVal - aVal;
      });
      for (const link of sortedAssetLinks) {
        // TODO rollback on error
        // it has been a year and a half we are never going to do this
        await this.handleLinkAction(assetType, assetId || assetResId, link);
      }
      return { ...assetActions, assetResult };
    } catch (error) {
      const axiosError = error as AxiosError;
      const { response, message, stack } = axiosError;
      const errorProps: WorkflowActionError = {
        errorType: 'serverError',
        stack,
        message,
        result: { ...assetActions, error },
      };
      if (response && response.status && response.status >= 400 && response.status >= 499) {
        errorProps.errorType = 'badRequest';
      }
      if (response && response.status && (response.status === 504 || response.status === 503)) {
        errorProps.errorType = 'timeout';
      }
      // A 409 is a referential-integrity conflict (e.g. an address still linked to other
      // resources), not a server error — surface it as a clear "still in use" message.
      if (response && response.status === 409) {
        errorProps.errorType = 'conflict';
      }
      const result: AssetActionPayloadResult = { ...assetActions, error: errorProps };
      return result;
    }
  }

  checkV2BaseRoute(assetType: AssetTypes, assetActionType?: AssetActionType) {
    const v2Entities = new Set(['carrierAccount']);
    if (v2Entities.has(assetType)) {
      return true;
    }
    // Address has a v2 endpoint only for delete (the gated archive-delete). Create/update/link
    // stay on v1, where they are unchanged.
    return assetType === 'address' && assetActionType === 'delete';
  }

  public async handleTenantLinkAction(
    parentAssetType: AssetTypes,
    parentAssetId: string | undefined,
    link: AssetActionPayloadLink,
  ) {
    const unlink = link.unlink || link.assetId === GLOBAL_TENANT;
    const parentUrlName = AssetService.assetTypeToAssetUrlName(parentAssetType);
    const tenants = await this.fetchTenants({});
    if (typeof link.assetId !== 'string') {
      throw new Error('Recieved Inavlid Asset ID');
    }
    const assetId: string = link.assetId;
    // find the tenant that is linked to the asset
    // this approach would fall apart
    // if assets started belonging to mulitple tenants
    const tenantToUnlink = tenants.find((t) => {
      if (parentAssetId) {
        return new Set(t?.links![parentUrlName]?.map((val) => val.shipiumId) ?? []).has(parentAssetId);
      }
      return false;
    });
    // if the asset is already linked to a tenant
    // delete that existing tenant
    if (parentAssetId && tenantToUnlink?.links![parentUrlName]) {
      const destroyBody = {
        // TODO talk with Lee about naming inconsistencies
        // returns shipiumId but accepts id
        [parentUrlName]: [{ id: parentAssetId }],
      };
      await this.deleteTenantLink(tenantToUnlink.tenantId!, destroyBody);
    }
    // if the user has selected a tenant to link
    // and it is not specified as global
    if (!unlink && parentAssetId) {
      const createBody = {
        [parentUrlName]: [
          {
            id: parentAssetId,
          },
        ],
      };
      await this.createTenantLink(assetId, createBody);
    }
  }

  public async handleLinkAction(
    parentAssetType: AssetTypes,
    parentAssetId: string | undefined,
    link: AssetActionPayloadLink,
  ) {
    if (link?.linkType === 'Tenant') {
      return await this.handleTenantLinkAction(parentAssetType, parentAssetId, link);
    } else {
      const baseRoute = `/partner-management-proxy/api/${this.checkV2BaseRoute(parentAssetType) ? 'v2' : 'v1'}/partners`;
      const parentUrlName = AssetService.assetTypeToAssetUrlName(parentAssetType);
      const linkUrlName = link.linkAssetTypeOverride || AssetService.assetTypeToAssetUrlName(link.assetType);
      if (!parentAssetId) {
        throw new Error('Error linking, parentAssetId not provided');
      }

      const res = await GatewayAxiosClient(
        `${baseRoute}/${parentUrlName}/${parentAssetId}/${linkUrlName}/${link.assetId}`,
        {
          method: link.unlink ? 'DELETE' : 'PUT',
        },
      );
      return res.data;
    }
  }
}
